import { useTranslation } from 'react-i18next'

import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  toast,
} from '@/components/ui'

import type { BillingOverview } from '../billingApi'
import { useBillingErrorText } from '../billingErrors'
import { formatCount } from '../format'
import { useOrderCreditPack } from '../queries'

/** Interações extras (`04` §4.4 item 4): só com assinatura; a compra gera uma fatura única. */
export function PacksSection({
  tenantId,
  overview,
}: {
  readonly tenantId: string
  readonly overview: BillingOverview
}) {
  const { t, i18n } = useTranslation()
  const errorText = useBillingErrorText()
  const order = useOrderCreditPack(tenantId)

  if (overview.creditPacks.length === 0) {
    return null
  }

  const status = overview.subscription?.status
  const allowed = status === 'Active' || status === 'PastDue'
  const money = (value: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: overview.currency }).format(
      value,
    )

  return (
    <section className="flex flex-col gap-3" aria-labelledby="billing-packs-title">
      <div className="flex flex-col gap-1">
        <h2 id="billing-packs-title" className="text-base font-semibold text-foreground-primary">
          {t('billing.packs.title')}
        </h2>
        <p className="text-sm text-foreground-muted">
          {t('billing.packs.subtitle', { months: 12 })}
        </p>
      </div>
      {!allowed ? <Alert variant="info" title={t('billing.packs.requiresPlan')} /> : null}
      <div className="grid gap-4 md:grid-cols-3">
        {overview.creditPacks.map((pack) => (
          <Card key={pack.code} data-testid={`pack-${pack.code}`}>
            <CardHeader>
              <CardTitle>
                {t('billing.packs.credits', { value: formatCount(pack.credits, i18n.language) })}
              </CardTitle>
              <CardDescription>{money(pack.price)}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                size="sm"
                disabled={
                  !allowed || !overview.canManage || !overview.gatewayConfigured || order.isPending
                }
                onClick={() => {
                  order.mutate(pack.code, {
                    onSuccess: () => {
                      toast.success(t('billing.packs.ordered'))
                    },
                    onError: (error) => {
                      toast.error(errorText(error))
                    },
                  })
                }}
              >
                {t('billing.packs.buy')}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  )
}
